import Polyglot from "node-polyglot";

export type LocaleCode =
  | "te-ST"
  | "de"
  | "en"
  | "es"
  | "fr"
  | "it"
  | "nb"
  | "nl"
  | "pt"
  | "ru"
  | "zh";

export interface ILocaleMap {
  intl: string;
  polyglot: string;
}

export interface ILocale {
  /**
   * The parsed string for the internally used rapport locale
   *
   * @returns {string}
   * @memberof ILocale
   */
  toString(): string;

  /**
   * The parsed string for the polyglot locale
   *
   * List of polyglot plural type codes (locales) here:
   * https://github.com/airbnb/polyglot.js/blob/3ca7e08c3771e058155ff9e4c3f289988da1190a/index.js#L96
   *
   * @returns {string}
   * @memberof ILocale
   */
  toPolyglotLocale(): string;

  /**
   * The parsed string for the intl locale
   *
   * List of intl supported locales
   * https://github.com/andyearnshaw/Intl.js/tree/master/locale-data/json
   *
   * @returns {string}
   * @memberof ILocale
   */
  toIntlLocale(): string;
}

export class Locale implements ILocale {
  public static readonly localeCodes: LocaleCode[] = [
    "te-ST",
    "de",
    "en",
    "es",
    "fr",
    "it",
    "nb",
    "nl",
    "pt",
    "ru",
    "zh"
  ];

  /**
   * Returns whether the passed localeCode is valid and supported
   *
   * @static
   * @param {(string | LocaleCode)} localeCode
   * @returns {boolean}
   * @memberof Locale
   */
  public static isValid(localeCode: string): boolean {
    if (!Locale.localeCodes.includes(localeCode as LocaleCode)) {
      return false;
    }

    return true;
  }

  /**
   * Returns a Locale created from the currently set polyglot locale
   *
   * @static
   * @param {Polyglot} polyglot
   * @returns {Locale}
   * @memberof Locale
   */
  public static fromPolyglot(polyglot: Polyglot): Locale {
    return Locale.from(polyglot.locale() as LocaleCode);
  }

  /**
   * Returns a Locale created from the passed string or locale code
   *
   * @static
   * @param {LocaleCode} localeCode
   * @returns {Locale}
   * @memberof Locale
   */
  public static from(localeCode: LocaleCode): Locale {
    if (!Locale.isValid(localeCode)) {
      throw new Error(`'${localeCode}' is not a known or supported locale`);
    }

    return new Locale(localeCode);
  }

  private constructor(public readonly code: LocaleCode) {}

  public toString(): string {
    return this.code;
  }

  public toPolyglotLocale(): string {
    return this.code;
  }

  public toIntlLocale(): string {
    return this.code;
  }
}
